import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Select from '@/components/atoms/Select';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import TextField from '@/components/atoms/TextField';
import type { ProviderFieldSchema } from '@/domain/providerConfig';
import { parseDate, toDateString } from '@/utils/date';

import { enumChoices } from './enumChoices';

import styles from './ProviderSchemaField.module.scss';

const TEXT_INPUT_MODE: Partial<Record<ProviderFieldSchema['kind'], 'url' | 'numeric'>> = {
    url: 'url',
    integer: 'numeric',
};

/**
 * The box of a single-value setting, inside the FormField that labels it and shows its hint
 * and error; the field gives it its id.
 */
export interface SchemaValueInputProps {
    field: ProviderFieldSchema;
    name: string;
    value: string;
    disabled: boolean;
    /** Notes under the field that describe the box besides its hint and error. */
    describedBy: string | undefined;
    placeholder: string | undefined;
    /** Shown and focusable, never changed: a save cannot write the field. */
    readOnly?: boolean;
    /** For the value box, such as monospace for a machine value. */
    valueClassName?: string;
    onBlur: () => void;
    onChange: (value: string) => void;
}

/** A single-value setting: a select for an enum, a date picker for a day, else a text box. */
function SchemaValueInput({
    field,
    name,
    value,
    disabled,
    describedBy,
    placeholder,
    readOnly = false,
    valueClassName,
    onBlur,
    onChange,
}: SchemaValueInputProps) {
    const { t } = useTranslation();
    if (field.kind === 'enum')
        return (
            <Select
                name={name}
                value={value}
                disabled={disabled}
                aria-required={field.required || undefined}
                aria-describedby={describedBy}
                onBlur={onBlur}
                onChange={(event) => onChange(event.target.value)}>
                {/* Empty means the default, so the empty option says which one. */}
                <option value="">{placeholder ?? ''}</option>
                {enumChoices(field, value).map((choice) => (
                    <option key={choice.value} value={choice.value}>
                        {choice.labelKey === null ? choice.value : t(choice.labelKey)}
                    </option>
                ))}
            </Select>
        );
    if (field.kind === 'date')
        return (
            // The picker has no disabled state of its own; a disabled fieldset turns off its
            // box and calendar button the native way.
            <fieldset className={styles.bareGroup} disabled={disabled}>
                <StyledSingleDatePicker
                    date={parseDate(value)}
                    placeholder={placeholder}
                    required={field.required}
                    // The picker hands back local noon; the stored value is that calendar day.
                    // Typing reports its own day below, so a null here is never taken as a clear.
                    onDateChange={(day) => {
                        if (day !== null) onChange(toDateString(day));
                    }}
                    // An emptied field clears the value. Text that is not a day stays in the
                    // form as typed, so the date rule rejects it and Save waits, instead of a
                    // typo saving as a cleared date.
                    onTextChange={(text, day) => {
                        if (day !== null) return;
                        onChange(text.trim() === '' ? '' : text.trim());
                    }}
                />
            </fieldset>
        );
    return (
        <TextField
            className={classNames(valueClassName, { [styles.readOnlyValue]: readOnly })}
            inputMode={TEXT_INPUT_MODE[field.kind]}
            autoComplete="off"
            spellCheck={false}
            name={name}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            aria-readonly={readOnly || undefined}
            aria-required={field.required || undefined}
            aria-describedby={describedBy}
            onBlur={onBlur}
            onChange={(event) => onChange(event.target.value)}
        />
    );
}

export default SchemaValueInput;
export { SchemaValueInput };
